import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3,
  List, ListOrdered, Link as LinkIcon, Quote, Minus, Palette,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo
} from 'lucide-react';

const colors = [
  '#000000', '#374151', '#64748B', '#DC2626',
  '#EA580C', '#D97706', '#16A34A', '#0891B2',
  '#2563EB', '#7C3AED', '#DB2777'
];

const ToolbarButton = ({ active, onClick, title, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`p-2 rounded-md transition-colors ${
      active ? 'bg-[#4ECDC4] text-white' : 'text-[#64748B] hover:bg-slate-100 hover:text-[#2D3748]'
    }`}
  >
    {children}
  </button>
);

const Divider = () => <div className="w-px h-6 bg-slate-200 mx-1" />;

export const RichTextEditor = ({ value, onChange, placeholder = 'Začněte psát...', testId = 'rich-text-editor' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] }
      }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#4ECDC4] underline' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] px-4 py-3',
        'data-testid': `${testId}-content`
      }
    }
  });

  // Keep editor in sync if value changes externally (e.g. when loaded)
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Odkaz (URL):', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Tučné (Ctrl+B)" testId="rte-bold"><Bold size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Kurzíva (Ctrl+I)" testId="rte-italic"><Italic size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Podtržené (Ctrl+U)" testId="rte-underline"><UnderlineIcon size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Nadpis 2" testId="rte-h2"><Heading2 size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Nadpis 3" testId="rte-h3"><Heading3 size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Odrážky" testId="rte-ul"><List size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Číslovaný seznam" testId="rte-ol"><ListOrdered size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Zarovnat vlevo" testId="rte-align-left"><AlignLeft size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Na střed" testId="rte-align-center"><AlignCenter size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Zarovnat vpravo" testId="rte-align-right"><AlignRight size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton active={editor.isActive('link')} onClick={setLink} title="Odkaz" testId="rte-link"><LinkIcon size={16} /></ToolbarButton>
        <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citace" testId="rte-quote"><Quote size={16} /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Oddělovač" testId="rte-hr"><Minus size={16} /></ToolbarButton>
        <Divider />
        <div className="relative group">
          <button type="button" title="Barva textu" className="p-2 rounded-md text-[#64748B] hover:bg-slate-100 hover:text-[#2D3748]" data-testid="rte-color">
            <Palette size={16} />
          </button>
          <div className="absolute top-full right-0 mt-1 hidden group-hover:flex group-focus-within:flex flex-wrap gap-1 p-2 bg-white rounded-lg shadow-lg border border-slate-200 w-[140px] z-50">
            {colors.map((c) => (
              <button key={c} type="button" onClick={() => editor.chain().focus().setColor(c).run()} className="w-5 h-5 rounded-full border border-slate-200 hover:scale-110 transition-transform" style={{ backgroundColor: c }} title={c} />
            ))}
            <button type="button" onClick={() => editor.chain().focus().unsetColor().run()} className="text-xs text-slate-500 underline mt-1 w-full">Reset</button>
          </div>
        </div>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Zpět (Ctrl+Z)" testId="rte-undo"><Undo size={16} /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Vpřed (Ctrl+Y)" testId="rte-redo"><Redo size={16} /></ToolbarButton>
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
};

export default RichTextEditor;
